import React, { useState, useEffect, MutableRefObject } from 'react';
import { useDispatch } from 'react-redux';
import { SwipeableHandlers } from 'react-swipeable';

import { Article } from '../../../../../types/items';
import { getArticleDocumentDownloadUrlRequest } from '../../../../../store/modules/articles/actions';
import MediaImage from './MediaImage';
import MediaOther from './MediaOther';
import MediaVideo from './MediaVideo';
import MediaPages from './MediaPages';
import MediaAudio from './MediaAudio';

/* eslint-disable @typescript-eslint/camelcase */

type Props = {
  media: Article;
  swipeable?: SwipeableHandlers;
  slideDirection: 'left' | 'right' | 'up' | 'down';
  isChangePreviewMedia: number | null;
  refPreviewContent: MutableRefObject<HTMLDivElement | null>;
  onPlayerPlay?: () => void;
  onPlayerPause?: () => void;
  onResizePlayer?: (props: { height: string; width: string }) => void;
};

/*
media_type_id:
  0 : "Other"
  1 : "Image"
  2 : "Video"
  3 : "Audio"
  4 : "Paged"
*/

const MediaPreview = ({
  media,
  swipeable,
  slideDirection,
  isChangePreviewMedia,
  refPreviewContent,
  onPlayerPause,
  onPlayerPlay,
  onResizePlayer,
}: Props) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const { document_id, document_download_url, video_id } = media;
  let media_type_id = media.ref.document?.media_type_id;
  const isVideo = video_id !== -1;
  const isProcessingMedia = media.ref.document?.ref?.is_processing || false;

  if (isVideo) {
    media_type_id = 2;
  }

  useEffect(() => {
    if (!isProcessingMedia) {
      if (media_type_id === 1 || media_type_id === 3 || (media_type_id === 2 && video_id === -1)) {
        if (document_download_url === undefined && !loading) {
          setLoading(true);
          dispatch(getArticleDocumentDownloadUrlRequest(document_id));
        }
        if (document_download_url !== undefined && loading) {
          setLoading(false);
        }
      }
    }
  }, [
    dispatch,
    document_id,
    document_download_url,
    video_id,
    loading,
    setLoading,
    media_type_id,
    isProcessingMedia,
  ]);

  switch (media_type_id) {
    case 0:
      return (
        <MediaOther
          media={media}
          swipeable={swipeable}
          slideDirection={slideDirection}
          isChangePreviewMedia={isChangePreviewMedia}
        />
      );

    case 1:
      return (
        <MediaImage
          signedUrl={document_download_url}
          swipeable={swipeable}
          slideDirection={slideDirection}
          isChangePreviewMedia={isChangePreviewMedia}
        />
      );

    case 2:
      return (
        <MediaVideo
          media={media}
          swipeable={swipeable}
          slideDirection={slideDirection}
          isChangePreviewMedia={isChangePreviewMedia}
          refPreviewContent={refPreviewContent}
          onPlay={onPlayerPlay}
          onPause={onPlayerPause}
          onResizePlayer={onResizePlayer}
        />
      );

    case 3:
      return (
        <MediaAudio
          signedUrl={document_download_url}
          swipeable={swipeable}
          slideDirection={slideDirection}
          isChangePreviewMedia={isChangePreviewMedia}
          refPreviewContent={refPreviewContent}
        />
      );

    case 4:
      return (
        <MediaPages
          media={media}
          swipeable={swipeable}
          slideDirection={slideDirection}
          isChangePreviewMedia={isChangePreviewMedia}
        />
      );

    default:
      return (
        <MediaOther
          media={media}
          swipeable={swipeable}
          slideDirection={slideDirection}
          isChangePreviewMedia={isChangePreviewMedia}
        />
      );
  }
};

export default MediaPreview;
